import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Eye,
  Copy,
} from "lucide-react";
import React, { useState } from "react";
import { FaWhatsapp } from "react-icons/fa";

const isPhoneColumn = (columnName?: string) => {
  if (!columnName) return false;
  const lower = columnName.toLowerCase();
  return (
    lower === "phone" ||
    lower === "phone_number" ||
    lower === "phone number" ||
    lower === "contact_number" ||
    lower === "contact number" ||
    lower === "sender_phone" ||
    lower === "mobile" ||
    lower === "mobile_number" ||
    lower === "mobile number" ||
    lower === "whatsapp" ||
    lower === "whatsapp_number"
  );
};

const getWhatsAppUrl = (phone: string) => {
  const digits = phone.replace(/[^\d]/g, "");
  return `https://wa.me/${digits}`;
};

const getColumnWidth = (column: any, data: any[]) => {
  const dataType = column.data_type?.toLowerCase() || "";
  const columnName = column.column_name?.toLowerCase() || "";

  const sampleValues = data
    .slice(0, Math.min(20, data.length))
    .map((row) => (row[column.column_name] ? String(row[column.column_name]) : ""));

  const dataTypeBadge = column.data_type?.split("(")[0] || "";
  const headerContentLength = column.column_name.length + dataTypeBadge.length + 10;
  const maxDataLength = Math.max(...sampleValues.map((val) => val.length), 5);
  const effectiveLength = Math.max(headerContentLength, maxDataLength);

  const isIdField =
    columnName.includes("id") ||
    columnName.includes("uuid") ||
    columnName.endsWith("_id") ||
    columnName.startsWith("id_");

  const getWidthClass = (minChars: number) => {
    if (minChars <= 8) return "min-w-20";
    if (minChars <= 12) return "min-w-24";
    if (minChars <= 16) return "min-w-32";
    if (minChars <= 20) return "min-w-40";
    if (minChars <= 25) return "min-w-48";
    if (minChars <= 30) return "min-w-56";
    if (minChars <= 40) return "min-w-64";
    if (minChars <= 50) return "min-w-72";
    if (minChars <= 70) return "min-w-80";
    return "min-w-96";
  };

  if (dataType.includes("bool") || dataType.includes("boolean")) {
    return `${getWidthClass(Math.max(effectiveLength, 8))} max-w-24`;
  } else if (isIdField || dataType.includes("uuid")) {
    return `${getWidthClass(effectiveLength)} max-w-80`;
  } else if (
    dataType.includes("int") ||
    dataType.includes("numeric") ||
    dataType.includes("decimal") ||
    dataType.includes("float") ||
    dataType.includes("double")
  ) {
    return `${getWidthClass(effectiveLength)} max-w-40`;
  } else if (dataType.includes("date") || dataType.includes("time") || dataType.includes("timestamp")) {
    return `${getWidthClass(Math.max(effectiveLength, 20))} max-w-56`;
  } else if (dataType.includes("text") || dataType.includes("varchar") || dataType.includes("char") || dataType.includes("string")) {
    if (dataType.includes("text")) {
      return `${getWidthClass(effectiveLength)} max-w-96`;
    } else {
      return `${getWidthClass(effectiveLength)} max-w-80`;
    }
  } else if (dataType.includes("json") || dataType.includes("jsonb")) {
    return `${getWidthClass(Math.max(effectiveLength, 40))} max-w-96`;
  } else {
    return `${getWidthClass(effectiveLength)} max-w-72`;
  }
};

const TextViewer = ({
  value,
  maxPreviewLength = 150,
}: {
  value: string;
  maxPreviewLength?: number;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const stringValue = String(value);
  const isLarge = stringValue.length > maxPreviewLength;
  const preview = isLarge ? `${stringValue.substring(0, maxPreviewLength)}...` : stringValue;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(stringValue);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (_err) {}
  };

  if (!isLarge && !isExpanded) {
    return <span className="text-slate-700">{stringValue}</span>;
  }

  return (
    <div className="relative group">
      <div className="relative">
        <div className={`text-slate-700 whitespace-pre-wrap break-words ${!isExpanded ? "line-clamp-3" : ""}`}>
          {isExpanded ? stringValue : preview}
        </div>
        <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="relative flex items-center space-x-1 px-2 py-1 bg-gradient-to-b from-black/70 via-black/60 to-black/50 rounded-md">
            {isLarge && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-6 px-2 text-xs text-white hover:text-white hover:bg-white/20"
              >
                <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                {isExpanded ? "Collapse" : "Expand"}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className="h-6 px-2 text-xs text-white hover:text-white hover:bg-white/20"
            >
              <Eye className="h-3 w-3 mr-1" />
              View
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={copyToClipboard}
              className="h-6 px-2 text-xs text-white hover:text-white hover:bg-white/20"
            >
              <Copy className="h-3 w-3 mr-1" />
              {copySuccess ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                <span>Text Viewer</span>
                <span className="text-sm text-slate-500 font-normal ml-2">
                  ({stringValue.length} characters)
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
                className="flex items-center space-x-1"
              >
                <Copy className="h-4 w-4" />
                <span>{copySuccess ? "Copied!" : "Copy All"}</span>
              </Button>
            </DialogTitle>
            <DialogDescription>Full text content view</DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh] bg-slate-50 rounded-lg p-4 border">
            <div className="text-slate-700 whitespace-pre-wrap break-words font-mono text-sm">
              {stringValue}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const JsonViewer = ({
  value,
  maxPreviewLength = 50,
}: {
  value: any;
  maxPreviewLength?: number;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  let jsonObj: any, formattedJson: string, isValidJson = true;
  try {
    jsonObj = typeof value === "string" ? JSON.parse(value) : value;
    formattedJson = JSON.stringify(jsonObj, null, 2);
  } catch {
    isValidJson = false;
    formattedJson = String(value);
  }

  const isLarge = formattedJson.length > maxPreviewLength;
  const preview = isLarge ? `${formattedJson.substring(0, maxPreviewLength)}...` : formattedJson;

  const getObjectInfo = () => {
    if (!isValidJson || !jsonObj) return null;
    if (Array.isArray(jsonObj)) return `Array (${jsonObj.length} items)`;
    if (typeof jsonObj === "object") return `Object (${Object.keys(jsonObj).length} properties)`;
    return null;
  };

  const objectInfo = getObjectInfo();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formattedJson);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (_err) {}
  };

  const renderJsonContent = (content: string) => {
    if (!isValidJson) {
      return <span className="text-slate-700 font-mono text-sm">{content}</span>;
    }
    const highlighted = content
      .replace(/"([^"]+)":/g, '<span class="text-blue-600 font-medium">"$1":</span>')
      .replace(/: "([^"]+)"/g, ': <span class="text-green-600">"$1"</span>')
      .replace(/: (true|false)/g, ': <span class="text-purple-600">$1</span>')
      .replace(/: (null)/g, ': <span class="text-red-500">$1</span>')
      .replace(/: (-?\d+(?:\.\d+)?)/g, ': <span class="text-orange-600">$1</span>');
    return (
      <pre
        className="font-mono text-xs leading-relaxed text-slate-600"
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    );
  };

  if (!isLarge && !isExpanded) {
    return (
      <div className="space-y-1">
        {objectInfo && <div className="text-xs text-slate-500 italic mb-1">{objectInfo}</div>}
        {renderJsonContent(formattedJson)}
      </div>
    );
  }

  return (
    <div className="relative group">
      {objectInfo && <div className="text-xs text-slate-500 italic mb-1">{objectInfo}</div>}
      <div className="relative">
        {renderJsonContent(isExpanded ? formattedJson : preview)}
        <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="relative flex items-center space-x-1 px-2 py-1 bg-gradient-to-b from-black/70 via-black/60 to-black/50 rounded-md">
            {isLarge && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-6 px-2 text-xs text-white hover:text-white hover:bg-white/20"
              >
                <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                {isExpanded ? "Collapse" : "Expand"}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className="h-6 px-2 text-xs text-white hover:text-white hover:bg-white/20"
            >
              <Eye className="h-3 w-3 mr-1" />
              View
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={copyToClipboard}
              className="h-6 px-2 text-xs text-white hover:text-white hover:bg-white/20"
            >
              <Copy className="h-3 w-3 mr-1" />
              {copySuccess ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                <span>JSON Viewer</span>
                {objectInfo && (
                  <span className="text-sm text-slate-500 font-normal ml-2">({objectInfo})</span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
                className="flex items-center space-x-1"
              >
                <Copy className="h-4 w-4" />
                <span>{copySuccess ? "Copied!" : "Copy All"}</span>
              </Button>
            </DialogTitle>
            <DialogDescription>
              Full JSON object view with syntax highlighting and formatting
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh] bg-slate-50 rounded-lg p-4 border">
            {renderJsonContent(formattedJson)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const formatCellContent = (value: any, column: any) => {
  const dataType = column.data_type?.toLowerCase() || "";
  const columnName = column.column_name?.toLowerCase() || "";

  if (value === null || value === undefined) {
    return (
      <span className="text-slate-400 italic font-medium bg-slate-50 px-2 py-1 rounded text-xs">
        null
      </span>
    );
  }

  const stringValue = String(value);

  if (dataType.includes("json")) {
    return <JsonViewer value={value} maxPreviewLength={80} />;
  }

  const isIdOrContactField =
    columnName.includes("id") ||
    columnName.includes("contact") ||
    columnName.includes("phone") ||
    columnName.includes("uuid") ||
    columnName.endsWith("_id") ||
    columnName.startsWith("id_");

  if (
    dataType.includes("int") ||
    dataType.includes("numeric") ||
    dataType.includes("decimal") ||
    dataType.includes("float") ||
    dataType.includes("double")
  ) {
    if (isIdOrContactField) {
      if (isPhoneColumn(column.column_name)) {
        return (
          <span className="text-slate-700 font-mono text-right flex items-center justify-end gap-1.5">
            {stringValue}
            <a
              href={getWhatsAppUrl(stringValue)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-700 flex-shrink-0"
              title="Chat on WhatsApp"
            >
              <FaWhatsapp size={14} />
            </a>
          </span>
        );
      }
      return (
        <span className="text-slate-700 font-mono text-right block">{stringValue}</span>
      );
    }
    return (
      <span className="text-slate-700 font-mono text-right block">
        {new Intl.NumberFormat().format(Number(value))}
      </span>
    );
  }

  if (dataType.includes("bool") || dataType.includes("boolean")) {
    const boolValue = value === true || value === "true" || value === "t" || value === 1;
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
          boolValue ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}
      >
        {boolValue ? "True" : "False"}
      </span>
    );
  }

  if (dataType.includes("date") || dataType.includes("time") || dataType.includes("timestamp")) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        if (dataType.includes("timestamp") || dataType.includes("datetime")) {
          return <span className="text-slate-700 font-mono text-sm">{date.toLocaleString()}</span>;
        } else if (dataType.includes("time")) {
          return <span className="text-slate-700 font-mono text-sm">{date.toLocaleTimeString()}</span>;
        } else {
          return <span className="text-slate-700 font-mono text-sm">{date.toLocaleDateString()}</span>;
        }
      }
    } catch {
      // Fall through to default
    }
  }

  if (stringValue.startsWith("http://") || stringValue.startsWith("https://")) {
    return (
      <a
        href={stringValue}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline text-sm"
      >
        {stringValue.length > 50 ? `${stringValue.substring(0, 50)}...` : stringValue}
      </a>
    );
  }

  if (stringValue.includes("@") && stringValue.includes(".") && !stringValue.includes(" ")) {
    return (
      <a href={`mailto:${stringValue}`} className="text-blue-600 hover:text-blue-800 underline text-sm">
        {stringValue}
      </a>
    );
  }

  if (isPhoneColumn(column.column_name) && stringValue.replace(/[\s\-\+\(\)]/g, "").match(/^\d{7,15}$/)) {
    return (
      <span className="text-slate-700 font-mono flex items-center gap-1.5">
        {stringValue}
        <a
          href={getWhatsAppUrl(stringValue)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-green-600 hover:text-green-700 flex-shrink-0"
          title="Chat on WhatsApp"
        >
          <FaWhatsapp size={14} />
        </a>
      </span>
    );
  }

  return <TextViewer value={stringValue} maxPreviewLength={200} />;
};

interface Column {
  column_name: string;
  data_type: string;
  is_nullable?: string;
}

interface DataTableRendererProps {
  data: any[];
  columns?: Column[];
  frozenColumnsCount?: number;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (columnName: string) => void;
  maxHeight?: string;
}

export function DataTableRenderer({
  data,
  columns,
  frozenColumnsCount = 0,
  sortColumn,
  sortDirection,
  onSort,
}: DataTableRendererProps) {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const effectiveColumns: Column[] = React.useMemo(() => {
    if (columns && columns.length > 0) return columns;
    if (data.length === 0) return [];
    const firstRow = data[0];
    return Object.keys(firstRow).map((key) => ({
      column_name: key,
      data_type: typeof firstRow[key],
      is_nullable: "YES",
    }));
  }, [columns, data]);

  const checkScrollPosition = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  React.useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", checkScrollPosition);
    window.addEventListener("resize", checkScrollPosition);
    return () => {
      container.removeEventListener("scroll", checkScrollPosition);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, [data, checkScrollPosition]);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 300, behavior: "smooth" });
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p className="text-sm">No data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <div className="border border-slate-200/60 rounded bg-white/80 shadow-sm relative flex-1 min-h-0">
        {showLeftArrow && (
          <button
            onClick={scrollLeft}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-40 bg-white/95 hover:bg-blue-50 border border-slate-300 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5 text-slate-700" />
          </button>
        )}
        {showRightArrow && (
          <button
            onClick={scrollRight}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-40 bg-white/95 hover:bg-blue-50 border border-slate-300 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5 text-slate-700" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className="overflow-auto h-full overscroll-x-contain"
        >
          <Table noWrapper className="min-w-max table-auto relative">
            <TableHeader className="sticky top-0 z-30 bg-slate-100 border-b">
              <TableRow className="border-b border-slate-200/60">
                {effectiveColumns.map((column, columnIndex) => (
                  <TableHead
                    key={column.column_name}
                    className={`whitespace-nowrap px-6 py-4 border-b border-slate-200 ${
                      columnIndex >= frozenColumnsCount ? "border-r border-slate-200" : ""
                    } ${getColumnWidth(column, data)}`}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center space-x-1">
                        {onSort ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-0 h-auto font-semibold text-slate-700 hover:text-slate-900 transition-colors duration-200 flex items-center"
                            onClick={() => onSort(column.column_name)}
                          >
                            <span className="truncate">{column.column_name}</span>
                            {sortColumn === column.column_name &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-3 w-3 ml-1 text-blue-600 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="h-3 w-3 ml-1 text-blue-600 flex-shrink-0" />
                              ))}
                          </Button>
                        ) : (
                          <span className="font-semibold text-slate-700">{column.column_name}</span>
                        )}
                      </div>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row, index) => (
                <TableRow
                  key={index}
                  className="hover:bg-slate-50/50 transition-colors duration-200 border-b border-slate-200"
                >
                  {effectiveColumns.map((column, columnIndex) => (
                    <TableCell
                      key={column.column_name}
                      className={`px-6 py-2 ${
                        columnIndex >= frozenColumnsCount ? "border-r border-slate-200" : ""
                      } ${getColumnWidth(column, data)}`}
                    >
                      <div className="overflow-hidden" title={String(row[column.column_name] || "")}>
                        {formatCellContent(row[column.column_name], column)}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
